import React from "react";
import { Box, Typography } from "@mui/material";
import {
  CustomButton,
  CustomModal,
  useSharedColors,
} from "@ippon/shared-components";
import { AREA_TYPE_LABELS, type AreaType } from "@ippon/shared-types";

/** 同一顧客 (customer_id) が紐づく他のアクティブ契約 1 件分の表示情報。 */
export interface LinkedContractSummary {
  contract_id: string;
  /** 物件名 (例: AAA物件) */
  property_name: string;
  /** 場所区分 (area_type)。チップ表示に利用 (専有部 / 共用部 / 建物設備 / 敷地 / その他) */
  area_type?: string;
  /** 名称 (= landmark_name, 例: 101号室) */
  unit_name: string;
}

export interface LinkedCustomerEditConfirmModalProps {
  open: boolean;
  /** 編集中の契約以外で、同じ契約者が紐づくアクティブ契約 */
  linkedContracts: LinkedContractSummary[];
  /** 影響先の取得中 / 更新処理中 */
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * 顧客マスタ (氏名・連絡先) を編集したとき、同じ契約者が他の場所区分/物件にも
 * 紐づいている場合に「他の登録先にも変更が波及する」ことを確認するモーダル。
 *
 * 表示のみ。影響先一覧の取得・確定時の updateCustomer 呼び出しは呼び出し側が担う。
 * 契約条件・契約履歴(過去エントリ)には波及しない。各アクティブ契約には BE 側で
 * tenant_change(契約者更新) として履歴が追記される。
 */
const LinkedCustomerEditConfirmModal: React.FC<
  LinkedCustomerEditConfirmModalProps
> = ({ open, linkedContracts, loading = false, onCancel, onConfirm }) => {
  const colors = useSharedColors();

  return (
    <CustomModal
      title="契約者情報の変更確認"
      openModal={open}
      handleCloseModal={onCancel}
      bodyText=""
      hideCloseButton
      modalType="deleteModal"
    >
      <Box sx={{ px: 1 }}>
        <Typography
          sx={{ fontSize: "14px", color: colors.text.primary, mb: 2 }}
        >
          以下の契約にも同じ契約者が登録されています。
          <br />
          変更はすべてに反映されます。
        </Typography>

        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            mb: 1,
            p: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {linkedContracts.map((c) => {
            const areaLabel = c.area_type
              ? AREA_TYPE_LABELS[c.area_type as AreaType]
              : undefined;
            return (
              <Box
                component="li"
                key={c.contract_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: colors.background.light,
                  fontSize: "14px",
                  color: colors.text.primary,
                }}
              >
                <span>{c.property_name}</span>
                {areaLabel && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.25,
                      borderRadius: "10px",
                      fontSize: "11px",
                      fontWeight: 700,
                      lineHeight: 1.4,
                      backgroundColor: colors.primary.light,
                      color: colors.primary.main,
                      border: `1px solid ${colors.primary.main}`,
                      flexShrink: 0,
                    }}
                  >
                    {areaLabel}
                  </Box>
                )}
                {c.unit_name && <span>{c.unit_name}</span>}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <CustomButton
          label="戻る"
          buttonCategory="cancelModal"
          onClick={onCancel}
          disabled={loading}
        />
        {/* 削除モーダルと同じ配分: cancelModal(50%) と対になるよう 50% Box でラップ */}
        <Box
          sx={{
            width: "50%",
            "& .MuiButton-root": { width: "100% !important" },
          }}
        >
          <CustomButton
            label="変更する"
            buttonCategory="submit"
            onClick={onConfirm}
            disabled={loading}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default LinkedCustomerEditConfirmModal;
